/* RESPONSIBLE TEAM: team-ai-agent */
import Service, { inject as service } from '@ember/service';
import type AdminWithPermissions from 'embercom/objects/inbox/admin-with-permissions';
import { request } from 'embercom/lib/inbox/requests';
import { AsyncData } from 'embercom/resources/utils/async-data';
import { use } from 'ember-resources/util/function-resource';
import type Admin from 'embercom/models/admin';
import type Conversation from 'embercom/objects/inbox/conversation';

export type CopilotWorkspaceUsageResponse = {
  admin_usages: Record<Admin['id'], number>;
  admin_translations_usages: Record<Admin['id'], number>;
};

export const COPILOT_FREE_CREDIT_LIMIT = 10;
export const TRANSLATIONS_FREE_CREDIT_LIMIT = 10;

export default class CopilotUsageService extends Service {
  @service declare appService: any;

  hasCopilotCreditsLeft(admin: AdminWithPermissions | Admin) {
    if (admin.hasUnlimitedCopilotAccess) {
      return true;
    }

    if (admin.hasLimitedCopilotAccess) {
      let usage = this.getAdminUsedCopilotCreditsCount(String(admin.id));
      let limit = COPILOT_FREE_CREDIT_LIMIT;
      return usage < limit;
    }

    return false; // No copilot access
  }

  getAdminUsedCopilotCreditsCount(admin_id: Admin['id']): number {
    let usage = this.copilotUsageDataLoader.value?.admin_usages ?? {};
    return usage[String(admin_id)] ?? 0;
  }

  hasTranslationsLeft(admin: AdminWithPermissions | Admin, conversation?: Conversation) {
    if (admin.hasUnlimitedCopilotAccess) {
      return true;
    }

    if (admin.hasLimitedCopilotAccess) {
      let usage = conversation
        ? this.getAdminUsedTranslationsCountFromConversation(conversation)
        : this.getAdminUsedTranslationsCount(String(admin.id));
      let limit = TRANSLATIONS_FREE_CREDIT_LIMIT;
      return usage < limit;
    }

    return false; // No copilot access
  }

  getAdminUsedTranslationsCount(admin_id: Admin['id']): number {
    let usage = this.copilotUsageDataLoader.value?.admin_translations_usages ?? {};
    return usage[String(admin_id)] ?? 0;
  }

  getAdminUsedTranslationsCountFromConversation(conversation: Conversation): number {
    return conversation.translatedConversationsCountByAdmin ?? 0;
  }

  async refreshCopilotUsageData() {
    if (!this.copilotUsageDataLoader.isFirstLoad) {
      this.copilotUsageDataLoader.reload();
    }
  }

  @use private copilotUsageDataLoader = AsyncData<CopilotWorkspaceUsageResponse>(
    async ({ signal }) => {
      let response = await request(
        `/ember/copilot/workspace_usage?app_id=${this.appService.app.id}`,
        { signal },
      );
      return response.json();
    },
  );
}

declare module '@ember/service' {
  interface Registry {
    CopilotUsageService: CopilotUsageService;
    'copilot-usage-service': CopilotUsageService;
  }
}
